import React, { useState, useEffect } from 'react';
import { CountryData } from '../data/countryData';
import { useNumberInput } from '../hooks/useNumberInput';
import ComparisonButton from './ComparisonButton';
import './SalarySearchSidebar.css';
import './UnifiedSidebar.css';

interface SalarySearchSidebarProps {
  countries: CountryData[];
  onCountrySelect: (country: CountryData) => void;
  darkMode: boolean;
}

interface SearchFilters {
  minSalary: number;
  maxSalary: number;
  minJobsCount: number;
  maxCostOfLiving: number;
  workLifeBalance: number;
  jobSecurity: number;
  healthcare: string;
  remotePercentage: number;
}

const SalarySearchSidebar: React.FC<SalarySearchSidebarProps> = ({ 
  countries, 
  onCountrySelect, 
  darkMode 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<SearchFilters>({
    minSalary: 0,
    maxSalary: 200000,
    minJobsCount: 0,
    maxCostOfLiving: 150,
    workLifeBalance: 0,
    jobSecurity: 0,
    healthcare: 'all',
    remotePercentage: 0
  });
  
  const minSalaryInput = useNumberInput({
    initialValue: 0,
    min: 0,
    max: 1000000,
    step: 1000,
    allowEmpty: true,
    onValidChange: (value) => setFilters(prev => ({ ...prev, minSalary: value }))
  });
  
  const maxSalaryInput = useNumberInput({
    initialValue: 200000,
    min: 0,
    max: 1000000,
    step: 1000,
    allowEmpty: true,
    onValidChange: (value) => setFilters(prev => ({ ...prev, maxSalary: value }))
  });
  
  const [filteredCountries, setFilteredCountries] = useState<CountryData[]>(countries);
  const [sortBy, setSortBy] = useState<'salary' | 'jobs' | 'savings' | 'workLife'>('salary');

  // Calculate potential savings for each country
  const calculateSavings = (country: CountryData, salary: number = country.avgSalary) => {
    const monthlySalary = salary / 12;
    const totalMonthlyCosts = Object.values(country.livingCosts).reduce((sum, cost) => sum + cost, 0);
    return (monthlySalary - totalMonthlyCosts) * 12;
  };

  // Calculate remote work percentage
  const calculateRemotePercentage = (country: CountryData) => {
    return Math.round((country.marketStats.remoteJobs / country.marketStats.totalJobs) * 100);
  };

  useEffect(() => {
    let filtered = countries.filter(country => {
      const matchesSearch = country.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesSalary = country.avgSalary >= filters.minSalary && country.avgSalary <= filters.maxSalary;
      const matchesJobs = country.marketStats.totalJobs >= filters.minJobsCount;
      const matchesCostOfLiving = country.costOfLivingIndex <= filters.maxCostOfLiving;
      const matchesWorkLife = country.marketStats.workLifeBalance >= filters.workLifeBalance;
      const matchesJobSecurity = country.marketStats.jobSecurity >= filters.jobSecurity;
      const matchesHealthcare = filters.healthcare === 'all' || country.marketStats.healthcare === filters.healthcare;
      const remotePerc = calculateRemotePercentage(country);
      const matchesRemote = remotePerc >= filters.remotePercentage;

      return matchesSearch && matchesSalary && matchesJobs && matchesCostOfLiving && 
             matchesWorkLife && matchesJobSecurity && matchesHealthcare && matchesRemote;
    });

    // Sort countries
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'salary':
          return b.avgSalary - a.avgSalary;
        case 'jobs':
          return b.marketStats.totalJobs - a.marketStats.totalJobs;
        case 'savings':
          return calculateSavings(b) - calculateSavings(a);
        case 'workLife':
          return b.marketStats.workLifeBalance - a.marketStats.workLifeBalance;
        default:
          return 0;
      }
    });

    setFilteredCountries(filtered);
  }, [countries, searchQuery, filters, sortBy]);

  const formatCurrency = (amount: number, currencySymbol: string) => {
    return `${currencySymbol}${(amount / 1000).toFixed(0)}k`;
  };

  const getSavingsColor = (savings: number) => {
    if (savings > 20000) return '#2ecc71';
    if (savings > 10000) return '#f39c12';
    if (savings > 0) return '#e67e22';
    return '#e74c3c';
  };

  return (
    <div className={`unified-sidebar salary-search-sidebar ${darkMode ? 'dark' : 'light'}`}>
      <div className="sidebar-header">
        <h2><span className="material-icons">public</span> Global Salary Explorer</h2>
        <p>Find your ideal work destination</p>
      </div>
      
      <div className="unified-sidebar-content">

      {/* Search and Filters */}
      <div className="unified-content-section search-filter-group">
        <div className="search-section">
          <input
            type="text"
            placeholder="Search countries..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filters-toggle">
          <button 
            className="toggle-filters-btn"
            onClick={() => setShowFilters(!showFilters)}
          >
            <span className="material-icons">{showFilters ? 'expand_less' : 'expand_more'}</span>
            Filters
          </button>
        </div>
      </div>

      {/* Quick Filters */}
      {showFilters && (
      <div className="unified-content-section quick-filters">
        <h4>Quick Filters</h4>
        <div className="filter-buttons">
          <button 
            className={`filter-btn ${filters.healthcare === 'public' ? 'active' : ''}`}
            onClick={() => setFilters(prev => ({ 
              ...prev, 
              healthcare: prev.healthcare === 'public' ? 'all' : 'public' 
            }))}
          >
            <span className="material-icons">local_hospital</span>
            Public Healthcare
          </button>
          <button 
            className={`filter-btn ${filters.workLifeBalance >= 8 ? 'active' : ''}`}
            onClick={() => setFilters(prev => ({ 
              ...prev, 
              workLifeBalance: prev.workLifeBalance >= 8 ? 0 : 8 
            }))}
          >
            <span className="material-icons">balance</span>
            Great Work-Life
          </button>
          <button 
            className={`filter-btn ${filters.remotePercentage >= 40 ? 'active' : ''}`}
            onClick={() => setFilters(prev => ({ 
              ...prev, 
              remotePercentage: prev.remotePercentage >= 40 ? 0 : 40 
            }))}
          >
            <span className="material-icons">home_work</span>
            Remote Friendly
          </button>
        </div>
      </div>
      )}

      {/* Advanced Filters */}
      {showFilters && (
      <div className="unified-content-section advanced-filters">
        <h4>Advanced Filters</h4>
        
        <div className="filter-group">
          <label>Salary Range</label>
          <div className="range-inputs">
            <div className={`input-wrapper ${!minSalaryInput.isValid ? 'error' : ''}`}>
              <input
                type="number"
                placeholder="Min"
                value={minSalaryInput.value}
                onChange={minSalaryInput.onChange}
                onBlur={minSalaryInput.onBlur}
                min="0"
                step="1000"
              />
              {minSalaryInput.error && (
                <span className="input-error">{minSalaryInput.error}</span>
              )}
            </div>
            <span>-</span>
            <div className={`input-wrapper ${!maxSalaryInput.isValid ? 'error' : ''}`}>
              <input
                type="number"
                placeholder="Max"
                value={maxSalaryInput.value}
                onChange={maxSalaryInput.onChange}
                onBlur={maxSalaryInput.onBlur}
                min="0"
                step="1000"
              />
              {maxSalaryInput.error && (
                <span className="input-error">{maxSalaryInput.error}</span>
              )}
            </div>
          </div>
        </div>

        <div className="filter-group">
          <label>Min Job Opportunities: {filters.minJobsCount.toLocaleString()}</label>
          <input
            type="range"
            min="0"
            max="50000"
            step="1000"
            value={filters.minJobsCount}
            onChange={(e) => setFilters(prev => ({ 
              ...prev, 
              minJobsCount: Number(e.target.value) 
            }))}
          />
        </div>

        <div className="filter-group">
          <label>Max Cost of Living: {filters.maxCostOfLiving}</label>
          <input
            type="range"
            min="50"
            max="200"
            step="5"
            value={filters.maxCostOfLiving}
            onChange={(e) => setFilters(prev => ({ 
              ...prev, 
              maxCostOfLiving: Number(e.target.value) 
            }))}
          />
        </div>
      </div>
      )}

      {/* Sort Options */}
      <div className="unified-content-section sort-section">
        <label>Sort by:</label>
        <select 
          value={sortBy} 
          onChange={(e) => setSortBy(e.target.value as any)}
          className="sort-select"
        >
          <option value="salary">Average Salary</option>
          <option value="jobs">Job Opportunities</option>
          <option value="savings">Potential Savings</option>
          <option value="workLife">Work-Life Balance</option>
        </select>
      </div>

      {/* Results */}
      <div className="unified-content-section scrollable results-section">
        <div className="results-header">
          <h4>Results ({filteredCountries.length})</h4>
          {Object.values(filters).some(v => v !== 0 && v !== 200000 && v !== 150 && v !== 'all') && (
            <button 
              className="clear-filters-btn"
              onClick={() => setFilters({
                minSalary: 0,
                maxSalary: 200000,
                minJobsCount: 0,
                maxCostOfLiving: 150,
                workLifeBalance: 0,
                jobSecurity: 0,
                healthcare: 'all',
                remotePercentage: 0
              })}
            >
              Clear
            </button>
          )}
        </div>
        <div className="country-list">
          {filteredCountries.map(country => {
            const savings = calculateSavings(country);
            const remotePerc = calculateRemotePercentage(country);
            
            return (
              <div 
                key={country.id} 
                className="country-card"
                onClick={() => onCountrySelect(country)}
              >
                <div className="country-header">
                  <span className="flag">{country.flag}</span>
                  <div className="country-info">
                    <h5>{country.name}</h5>
                    <div className="salary-range">{country.avgSalaryRange}</div>
                  </div>
                  <div onClick={(e) => e.stopPropagation()}>
                    <ComparisonButton type="country" item={country} className="compact" />
                  </div>
                </div>
                
                <div className="country-stats">
                  <div className="stat">
                    <span className="label">Avg Salary:</span>
                    <span className="value">
                      {formatCurrency(country.avgSalary, country.economicIndicators.currencySymbol)}
                    </span>
                  </div>
                  
                  <div className="stat">
                    <span className="label">Jobs:</span>
                    <span className="value">{(country.marketStats.totalJobs / 1000).toFixed(0)}k</span>
                  </div>
                  
                  <div className="stat">
                    <span className="label">Savings:</span>
                    <span 
                      className="value"
                      style={{ color: getSavingsColor(savings) }}
                    >
                      {formatCurrency(savings, country.economicIndicators.currencySymbol)}
                    </span>
                  </div>
                  
                  <div className="stat">
                    <span className="label">Remote:</span>
                    <span className="value">{remotePerc}%</span>
                  </div>
                  
                  <div className="indicators">
                    <div className="indicator">
                      <span>Work-Life:</span>
                      <div className="rating">
                        {'★'.repeat(Math.round(country.marketStats.workLifeBalance / 2))}
                      </div>
                    </div>
                    <div className="indicator">
                      <span>Security:</span>
                      <div className="rating">
                        {'★'.repeat(Math.round(country.marketStats.jobSecurity / 2))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      </div>
    </div>
  );
};

export default SalarySearchSidebar;